<script lang="ts" setup>
import { ref, onMounted, reactive } from 'vue';
import { AppInitializationDto, AuthProviderV2, AuthenticationState } from '@mit/aws-vue';
import ConfirmDialog from 'primevue/confirmdialog';
import { STAGE_CONFIG } from './config';
import { useDuoAuthStore } from './stores/duoAuthStore';
import { useInitStore } from './stores/initStore';
import { useTextStore } from './stores/textsStore';

const initializeDto = reactive<AppInitializationDto>({
    isLoading: true,
    hasAccess: false,
    hasError: false
});

let isLoggedIn = ref(false);

const initStore = useInitStore();
const textsStore = useTextStore();

const init = async () => {
    try {
        await AuthProviderV2.configure(STAGE_CONFIG);

        const state = AuthProviderV2.getState();

        if (state === AuthenticationState.ERROR) {
            console.error('Error configuring auth');
            initializeDto.isLoading = false;
            initializeDto.hasError = true;
            return;
        }

        if (state === AuthenticationState.UNAUTHENTICATED) {
            await AuthProviderV2.signIn();
            return;
        }

        if (state === AuthenticationState.AUTHENTICATED) {
            // Load critical data in parallel (fire-and-forget so the UI renders quickly)
            initStore.loadInit().catch((error) => {
                console.warn('Init data load failed:', error);
            });

            textsStore.fetchTexts().catch((error) => {
                console.warn('Text fetch failed:', error);
            });

            // Check Duo callback separately (returns boolean, not data)
            const duoStore = useDuoAuthStore();
            duoStore.checkDuoCallback().catch((error: any) => {
                console.warn('Duo callback check failed:', error);
            });

            isLoggedIn.value = true;
            initializeDto.isLoading = false;
            initializeDto.hasAccess = true;
            initializeDto.hasError = false;
        }
    } catch (error) {
        console.error('App initialization failed:', error);
        initializeDto.isLoading = false;
        initializeDto.hasAccess = false;
        initializeDto.hasError = true;
        isLoggedIn.value = false;
    }
};

onMounted(() => {
    init();
});
</script>

<template>
    <ConfirmDialog />
    <!-- Show loading only during initial auth -->
    <div v-if="!isLoggedIn" class="flex items-center justify-center min-h-screen">
        <div class="text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-[32px]"></div>
            <p class="display-medium text-black/75">MIT Direct Deposit Preferences</p>
        </div>
    </div>
    <!-- Show app immediately after auth, with progressive enhancement -->
    <router-view v-else />
</template>

<style scoped></style>
